// src/pages/customers/CustomerDetailPage/CustomerDetailPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import CustomersAPI, { unwrapItem } from '../../../api/v3/customers-api-v3';
import { CustomerAddressesAPI } from '../../../api/v3/customers-api-v3';

import ManualDeleteButton, { MANUAL_DELETE_NOTE } from '../../../components/bagspa/ManualDeleteButton';

const BASE = '/app/sales/bagspa';

export default function CustomerDetailPage() {
  const { id } = useParams();               // customer id
  const history = useHistory();

  const [cust, setCust] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');

  // inline edit form
  const [form, setForm] = useState({ name: '', email: '', phone: '' });

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await CustomersAPI.show(id);
        const item = unwrapItem(res);
        if (ignore) return;
        setCust(item || null);
        setForm({ name: item?.name || '', email: item?.email || '', phone: item?.phone || '' });

        // load addresses
        try {
          const addr = await CustomerAddressesAPI.list(id);
          const arr  = Array.isArray(addr?.items) ? addr.items
                    : Array.isArray(addr?.data)  ? addr.data
                    : Array.isArray(addr)        ? addr
                    : [];
          if (!ignore) setAddresses(arr);
        } catch {
          const arr = Array.isArray(item?.addresses) ? item.addresses : [];
          if (!ignore) setAddresses(arr);
        }
      } catch (e) {
        if (!ignore) setError(e.message || 'Failed to load customer');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id]);

  const title = useMemo(() => (editing ? `Edit Customer #${id}` : `Customer #${id}`), [editing, id]);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  function validate() {
    const missing = [];
    const words = String(form.name || '').trim().split(/\s+/).filter(Boolean);
    if (words.length < 2) missing.push('Name (min 2 words)');
    if (!String(form.email).trim()) missing.push('Email');
    if (!String(form.phone).trim()) missing.push('Phone');
    if (form.email && !/.+@.+\..+/.test(form.email)) missing.push('Email (format)');
    return missing;
  }

  async function onSave() {
    const missing = validate();
    if (missing.length) {
      const msg = `Please fill required fields:\n- ${missing.join('\n- ')}`;
      setError(msg);
      alert(msg);
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = { name: form.name, email: form.email, phone: form.phone };
      await CustomersAPI.update(id, payload);
      setCust((prev) => ({ ...(prev || {}), ...payload }));
      setEditing(false);
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  // navigate to Address Edit when clicking an address card
  const openAddressEdit = (addrId, e) => {
    if (e?.metaKey || e?.ctrlKey) {
      window.open(`${BASE}/customers/${id}/addresses/${addrId}/edit`, '_blank', 'noopener');
    } else {
      history.push(`${BASE}/customers/${id}/addresses/${addrId}/edit`);
    }
  };

  if (loading) return <div>Loading…</div>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">{title}</h2>
        <Link className="btn btn-light" to={`${BASE}/customers`}>← Back</Link>
      </div>

      {error && <div className="alert alert-danger" style={{ whiteSpace: 'pre-wrap' }}>{error}</div>}

      {cust && (
        <div className="mb-3">
          <ManualDeleteButton kind="Customer" label={cust.name || `#${id}`} onDelete={() => CustomersAPI.destroy(id)} onDeleted={() => history.replace(`${BASE}/customers`)} />
          <p className="text-muted small mt-2">{MANUAL_DELETE_NOTE}</p>
        </div>
      )}

      <div className="row">
        {/* LEFT: Profile card -> click to edit */}
        <div className="col-lg-6">
          {!editing ? (
            <div
              className="card mb-4"
              role="button"
              tabIndex={0}
              onClick={() => setEditing(true)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setEditing(true)}
              style={{ cursor: 'pointer' }}
              title="Click to edit name, email, phone"
            >
              <div className="card-body">
                <div className="text-muted mb-2">Profile</div>
                <div className="mb-2"><strong>Name</strong><br />{cust?.name || '-'}</div>
                <div className="mb-2"><strong>Email</strong><br />{cust?.email || '-'}</div>
                <div className="mb-0"><strong>Phone</strong><br />{cust?.phone || '-'}</div>
              </div>
            </div>
          ) : (
            <div className="card mb-4">
              <div className="card-body">
                <div className="text-muted mb-2">Edit Profile</div>
                <div className="form-row">
                  <div className="form-group col-md-4">
                    <label>Name <span className="text-danger">*</span></label>
                    <input className="form-control" value={form.name} onChange={onChange('name')} required />
                  </div>
                  <div className="form-group col-md-4">
                    <label>Email <span className="text-danger">*</span></label>
                    <input type="email" className="form-control" value={form.email} onChange={onChange('email')} required />
                  </div>
                  <div className="form-group col-md-4">
                    <label>Phone <span className="text-danger">*</span></label>
                    <input className="form-control" value={form.phone} onChange={onChange('phone')} required />
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-secondary mr-2" onClick={() => { setEditing(false); setError(''); setForm({ name: cust?.name || '', email: cust?.email || '', phone: cust?.phone || '' }); }}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={onSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Address list -> click any card to open Address Edit */}
        <div className="col-lg-6">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="text-muted">Addresses</div>
            <Link className="btn btn-primary btn-sm" to={`${BASE}/customers/${id}/addresses/new`}>+ New Address</Link>
          </div>

          {addresses.map(a => (
            <div
              key={a.id}
              className="card mb-3"
              role="button"
              tabIndex={0}
              onClick={(e) => openAddressEdit(a.id, e)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openAddressEdit(a.id, e)}
              style={{ cursor: 'pointer' }}
              title="Click to edit this address"
            >
              <div className="card-body py-3">
                <div className="font-weight-bold">{a.alias || a.recipient_name || `Address #${a.id}`}</div>
                <div className="text-muted small">
                  {a.street || a.street_address || '-'}
                  <br />
                  {[a.subdistrict || a.sub_district, a.district, a.city, a.province]
                    .filter(Boolean).join(', ')}{a.postal_code ? ` • ${a.postal_code}` : ''}
                </div>
              </div>
            </div>
          ))}

          {addresses.length === 0 && (
            <div className="text-muted">No addresses.</div>
          )}
        </div>
      </div>
    </div>
  );
}
